import { supabase } from '../supabase';

/* ============================================
   PROMPTS
   ============================================ */

export const COACH_SYSTEM = (userName, skillBlock, profileBlock) => `You are the Mirror coach for Curx AI, a warm, perceptive AI guide who helps professionals see themselves more clearly.

Your job: have a deep, curious conversation with ${userName} to understand their professional self-perception, hidden strengths they don't credit themselves for, and where they secretly want to grow.

CONTEXT ABOUT THIS PERSON:
${skillBlock}${profileBlock}

Rules:
1. Ask ONE question at a time. Never stack questions.
2. Be warm and curious — NOT clinical or like a survey.
3. Reflect back briefly what you heard before the next question.
4. Keep messages SHORT — 2-3 sentences max.
5. Examples of the kind of questions to ask:
   - "When you've felt most alive at work, what were you actually doing?"
   - "What do colleagues come to you for that you take for granted?"
   - "If you could remove one constraint from your career path, what would it be?"
   - "What part of your skill set feels easiest to you, that others find hard?"
   - "Where do you feel like you're playing smaller than you could?"
6. Use the user's name occasionally, not every message.

After 4-5 thoughtful exchanges where you've understood their hidden strengths, growth edges, and a sense of where they want to go — signal that you're ready by ending your message with EXACTLY this token on its own line:
[READY_FOR_MIRROR]

When you signal [READY_FOR_MIRROR], the conversation ends and a separate Mirror reflection is generated. Do not write the reflection yourself in the chat.

Begin with a warm opening that invites them in.`;

export const MIRROR_PROMPT = (userName, transcript, skillBlock, profileBlock) => `You are Curx AI's Mirror module. Read the conversation below carefully — it's between you (the Mirror coach) and ${userName}, a professional reflecting on their career.

Generate a Career Mirror reflection based ONLY on what they actually said and the skill data shown.

CONTEXT:
${skillBlock}${profileBlock}

CONVERSATION:
${transcript}

Output ONLY valid JSON in this exact format. No markdown fences. No preamble. No explanation. Just the JSON object:

{
  "hidden_strengths": ["<strength 1 with brief why drawn from conversation>", "<strength 2 with why>", "<strength 3 with why>"],
  "growth_edges": ["<edge 1 with brief why>", "<edge 2 with why>", "<edge 3 with why>"],
  "top_3_moves": [
    {"role": "<concrete role title>", "why": "<2-sentence rationale specific to this person>"},
    {"role": "<role title>", "why": "<rationale>"},
    {"role": "<role title>", "why": "<rationale>"}
  ],
  "bold_question": "<one provocative reflection question for them to sit with — make it pointed and specific to what they shared>"
}

Be SPECIFIC to THIS person. No generic career advice. Reference their actual words and skills.`;

/* ============================================
   PROMPT BLOCK BUILDERS
   ============================================ */

export function buildSkillBlock(currentSkills) {
  if (!currentSkills || currentSkills.length === 0) {
    return 'Skills tracked: (none yet)';
  }
  const lines = currentSkills.map(s =>
    `- ${s.skill_name}: ${s.score}/100 (${s.category})`
  );
  return `Skills tracked (current scores):\n${lines.join('\n')}`;
}

export function buildProfileBlock(profile) {
  if (!profile) return '';
  const parts = [];
  if (profile.current_job_role) parts.push(`Current role: ${profile.current_job_role}`);
  if (profile.target_job_role)  parts.push(`Target role: ${profile.target_job_role}`);
  if (parts.length === 0) return '';
  return `\n\nRole context:\n${parts.map(p => `- ${p}`).join('\n')}`;
}

/* ============================================
   SUPABASE
   ============================================ */

export async function loadSkillProfile(userId) {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from('skill_profiles')
      .select('*')
      .eq('user_id', userId)
      .limit(1);
    if (error) return null; // Profile is optional — silent
    return (data && data[0]) || null;
  } catch (_) {
    return null;
  }
}

export async function loadMirrors(userId) {
  if (!userId) return [];
  const { data, error } = await supabase
    .from('skill_pulse_conversations')
    .select('*')
    .eq('user_id', userId)
    .eq('tab', 'mirror')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || [])
    .map(row => {
      const parsed = parseMirrorJSONSafe(row.output);
      if (!parsed) {
        // eslint-disable-next-line no-console
        console.warn('[SkillPulse Mirror] Skipping malformed row', row.id);
        return null;
      }
      return {
        id: row.id,
        created_at: row.created_at,
        conversation: row.conversation,
        metadata: row.metadata || {},
        mirror: parsed,
      };
    })
    .filter(Boolean);
}

export async function saveMirror({ user_id, conversation, mirror, metadata }) {
  if (!user_id) throw new Error('Missing user_id');
  const payload = {
    user_id,
    tab: 'mirror',
    conversation: conversation || [],
    output: JSON.stringify(mirror || {}),
    metadata: metadata || {},
  };
  const { data, error } = await supabase
    .from('skill_pulse_conversations')
    .insert(payload)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Save didn't write any rows. Check your Supabase RLS policy for INSERT on skill_pulse_conversations.");
  }
  return data[0];
}

/* ============================================
   PARSING & FORMATTING
   ============================================ */

function parseMirrorJSONSafe(text) {
  try { return parseMirrorJSON(text); } catch (_) { return null; }
}

export function parseMirrorJSON(text) {
  if (!text) throw new Error('Empty AI response');
  let parsed = null;
  try {
    parsed = JSON.parse(text);
  } catch (_) {
    // Try extracting a JSON object out of surrounding prose / fences
    const match = String(text).match(/\{[\s\S]*\}/);
    if (match) {
      try { parsed = JSON.parse(match[0]); } catch (__) { /* fallthrough */ }
    }
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Could not parse Mirror response as JSON');
  }
  if (!Array.isArray(parsed.hidden_strengths) || parsed.hidden_strengths.length === 0) {
    throw new Error('Mirror response missing hidden_strengths');
  }
  if (!Array.isArray(parsed.growth_edges) || parsed.growth_edges.length === 0) {
    throw new Error('Mirror response missing growth_edges');
  }
  if (!Array.isArray(parsed.top_3_moves) || parsed.top_3_moves.length === 0) {
    throw new Error('Mirror response missing top_3_moves');
  }
  if (!parsed.bold_question || typeof parsed.bold_question !== 'string') {
    throw new Error('Mirror response missing bold_question');
  }
  return parsed;
}

export function formatMirrorDate(timestamp) {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}
